import { apiClient, apiFetchWithMeta } from '@/lib/api-client';
import { downloadAuthenticatedFile } from '@/lib/download';
import type {
  AutomationCatalog,
  ParameterValue,
  Run,
  RunDetail,
  RunKind,
  RunLogPage,
  RunMode,
  RunStatus,
  RunStrategy,
  TargetPreview,
  TargetSelection,
} from '@/types/api';

export const AUTOMATION_CATALOG_KEY = ['automation', 'catalog'] as const;
export const RUNS_KEY = ['automation', 'runs'] as const;

export const runKey = (id: string) => ['automation', 'runs', id] as const;

export interface RunFilters {
  kind?: RunKind | '';
  subject?: string;
  status?: RunStatus | '';
  serverId?: string;
  cursor?: string;
  limit?: number;
}

/**
 * What starting a run sends. `command`, `run_as` and `timeout_seconds` belong to a command run;
 * `parameters` to a playbook or template.
 */
export interface StartRunInput {
  kind: RunKind;
  subject?: string;
  parameters?: Record<string, ParameterValue>;
  command?: string;
  run_as?: 'root' | 'user';
  mode: RunMode;
  strategy: RunStrategy;
  timeout_seconds?: number;
  targets: TargetSelection;
}

export const automationApi = {
  catalog: () => apiClient.get<AutomationCatalog>('/automation/catalog'),

  /**
   * Which servers a selection reaches, with what the platform already expects to go wrong on each.
   * Changes nothing and contacts no server.
   */
  previewTargets: (targets: TargetSelection, kind: RunKind | '' = '', subject = '') =>
    apiClient.post<TargetPreview>('/automation/targets/preview', { targets, kind, subject }),

  listRuns: async (filters: RunFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.kind) params.set('kind', filters.kind);
    if (filters.subject) params.set('subject', filters.subject);
    if (filters.status) params.set('status', filters.status);
    if (filters.serverId) params.set('server_id', filters.serverId);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.limit) params.set('limit', String(filters.limit));

    const query = params.toString();
    const result = await apiFetchWithMeta<{ items: Run[] }>(`/automation/runs${query ? `?${query}` : ''}`);

    const next = result.meta.next_cursor;

    return {
      items: result.data.items,
      nextCursor: typeof next === 'string' ? next : null,
    };
  },

  startRun: (input: StartRunInput) => apiClient.post<Run>('/automation/runs', input),

  getRun: (id: string) => apiClient.get<RunDetail>(`/automation/runs/${id}`),

  /** Only what one server printed after `after`, so a poll a second returns only what is new. */
  targetLog: (runId: string, targetId: string, after: number) =>
    apiClient.get<RunLogPage>(`/automation/runs/${runId}/targets/${targetId}/log?after=${after}`),

  confirmRun: (id: string) => apiClient.post<RunDetail>(`/automation/runs/${id}/confirm`),

  cancelRun: (id: string) => apiClient.post<RunDetail>(`/automation/runs/${id}/cancel`),

  /** A new run on the servers that did not succeed, pointing back at this one. */
  retryRun: (id: string) => apiClient.post<Run>(`/automation/runs/${id}/retry`),

  /** One server's whole log as a file, fetched with the session's token. */
  downloadTargetLog: (runId: string, targetId: string) =>
    downloadAuthenticatedFile(`/automation/runs/${runId}/targets/${targetId}/log/download`, 'run.log'),
};
